import { apiUrl } from '../config/api';
import { EResource } from '../types/departamento';
import {
	EMiRecursoStatus,
	IMiRecurso,
	IRecursoRevision,
} from '../types/miRecurso';
import { dateRangeParams, queryString, request } from './http';

export type ResourceUpload =
	| { tipo: 'archivo'; file: File }
	| { tipo: 'enlace'; url: string; nombre: string };

interface ResourceResponse {
	id: number;
	courseId: number;
	subjectId: number;
	subjectName: string;
	teacherId: number;
	teacherName: string;
	name: string;
	type: string;
	status: string;
	url: string | null;
	sizeBytes: number | null;
	version: number;
	reviewNote: string | null;
	reviewerName: string | null;
	reviewerEmail: string | null;
	createdAt: string;
	updatedAt: string;
}

const TYPE_TO_API: Record<EResource, string> = {
	[EResource.pdf]: 'PDF',
	[EResource.docx]: 'DOCX',
	[EResource.pptx]: 'PPTX',
	[EResource.xlsx]: 'XLSX',
	[EResource.video]: 'VIDEO',
	[EResource.imagen]: 'IMAGE',
	[EResource.link]: 'LINK',
	[EResource.other]: 'OTHER',
};

const TYPE_FROM_API: Record<string, EResource> = {
	PDF: EResource.pdf,
	DOCX: EResource.docx,
	PPTX: EResource.pptx,
	XLSX: EResource.xlsx,
	VIDEO: EResource.video,
	IMAGE: EResource.imagen,
	LINK: EResource.link,
	OTHER: EResource.other,
};

const STATUS_TO_API: Record<EMiRecursoStatus, string> = {
	en_revision: 'PENDING_REVIEW',
	aprobado: 'APPROVED',
	cambios_solicitados: 'CHANGES_REQUESTED',
	denegado: 'DENIED',
};

const STATUS_FROM_API: Record<string, EMiRecursoStatus> = {
	PENDING_REVIEW: 'en_revision',
	APPROVED: 'aprobado',
	CHANGES_REQUESTED: 'cambios_solicitados',
	DENIED: 'denegado',
};

export const formatBytes = (size: number | null): string => {
	if (size === null || size === undefined) return '—';
	if (size < 1024) return `${size} B`;
	if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
	return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const toMiRecurso = (row: ResourceResponse): IMiRecurso => ({
	id: row.id,
	name: row.name,
	resourceType: TYPE_FROM_API[row.type] ?? EResource.other,
	uploadDate: new Date(row.updatedAt),
	size: formatBytes(row.sizeBytes),
	materiaId: row.courseId,
	status: STATUS_FROM_API[row.status] ?? 'en_revision',
	reviewNote: row.reviewNote,
	reviewerName: row.reviewerName,
	reviewerEmail: row.reviewerEmail,
	url: row.url ?? undefined,
});

const toRevision = (row: ResourceResponse): IRecursoRevision => ({
	id: row.id,
	name: row.name,
	teacherName: row.teacherName,
	subjectId: row.subjectId,
	subjectName: row.subjectName,
	resourceType: TYPE_FROM_API[row.type] ?? EResource.other,
	uploadDate: new Date(row.updatedAt),
	size: formatBytes(row.sizeBytes),
	status: STATUS_FROM_API[row.status] ?? 'en_revision',
});

export interface ResourceListQuery {
	courseId?: number;
	search?: string;
	type?: EResource | 'all';
	status?: EMiRecursoStatus | 'all';
	dateRange?: string;
}

const toQuery = (query: ResourceListQuery): string =>
	queryString({
		courseId: query.courseId,
		search: query.search?.trim() || undefined,
		type: query.type && query.type !== 'all' ? TYPE_TO_API[query.type] : undefined,
		status: query.status && query.status !== 'all' ? STATUS_TO_API[query.status] : undefined,
		...(query.dateRange ? dateRangeParams(query.dateRange) : {}),
	});

export const listTeacherResources = (query: ResourceListQuery = {}): Promise<IMiRecurso[]> =>
	request<ResourceResponse[]>(`/api/teacher/resources${toQuery(query)}`).then((rows) =>
		rows.map(toMiRecurso)
	);

export const listStudentResources = (courseId: number): Promise<IMiRecurso[]> =>
	request<ResourceResponse[]>(`/api/courses/${courseId}/resources`).then((rows) => rows.map(toMiRecurso));

export const listReviewResources = (query: ResourceListQuery = {}): Promise<IRecursoRevision[]> =>
	request<ResourceResponse[]>(`/api/resources/review${toQuery(query)}`).then((rows) =>
		rows.map(toRevision)
	);

export const uploadResource = (courseId: number, input: ResourceUpload): Promise<IMiRecurso> => {
	if (input.tipo === 'archivo') {
		const body = new FormData();
		body.set('courseId', String(courseId));
		body.set('file', input.file);
		return request<ResourceResponse>('/api/teacher/resources', { method: 'POST', body }).then(toMiRecurso);
	}
	return request<ResourceResponse>('/api/teacher/resources', {
		method: 'POST',
		body: JSON.stringify({ courseId, name: input.nombre, url: input.url }),
	}).then(toMiRecurso);
};

export const replaceResource = (id: number, input: ResourceUpload): Promise<IMiRecurso> => {
	if (input.tipo === 'archivo') {
		const body = new FormData();
		body.set('file', input.file);
		return request<ResourceResponse>(`/api/teacher/resources/${id}/versions`, {
			method: 'POST',
			body,
		}).then(toMiRecurso);
	}
	return request<ResourceResponse>(`/api/teacher/resources/${id}/versions`, {
		method: 'POST',
		body: JSON.stringify({ name: input.nombre, url: input.url }),
	}).then(toMiRecurso);
};

export const deleteResource = (id: number): Promise<void> =>
	request(`/api/teacher/resources/${id}`, { method: 'DELETE' });

export const setResourceStatus = (
	id: number,
	status: Exclude<EMiRecursoStatus, 'en_revision'>,
	note?: string
): Promise<IRecursoRevision> =>
	request<ResourceResponse>(`/api/resources/${id}/status`, {
		method: 'PATCH',
		body: JSON.stringify({
			status: STATUS_TO_API[status],
			...(note ? { note } : {}),
		}),
	}).then(toRevision);

export const resourceDownloadUrl = (id: number): string => apiUrl(`/api/resources/${id}/download`);
